import { apiFetch } from "@healthos/api-client";
import { useEffect, useState } from "react";

export interface AttentionEvent {
  id: string;
  kind?: string;
  title?: string;
  body?: string;
  deepLink?: string | null;
  data?: {
    kind?: string;
    tier?: string;
    rationale?: string;
    mechanism?: string;
    triggeredBy?: string;
    action?: string;
    briefId?: string;
    url?: string;
    label?: string;
  };
}

interface AttentionState {
  events: AttentionEvent[];
}

interface UseAttentionEventsOptions {
  enabled?: boolean;
}

const RECONNECT_BACKOFF_MS = [1000, 2000, 5000, 10000, 15000];
const STOP_GRACE_MS = 500;

// One shared SSE connection for every consumer. The browser caps ~6
// connections per origin; a stream per hook caller (toast, nav badge, x
// StrictMode) exhausts that and starves every other request to core-api.
let refCount = 0;
let controller: AbortController | null = null;
let stopTimer: ReturnType<typeof setTimeout> | null = null;
let latest: AttentionState | undefined;
const listeners = new Set<(value: AttentionState | undefined) => void>();

function broadcast(value: AttentionState | undefined): void {
  latest = value;
  for (const listener of listeners) listener(value);
}

function startStream(): void {
  if (controller) return;
  const ctrl = new AbortController();
  controller = ctrl;
  let attempt = 0;

  const run = async () => {
    while (!ctrl.signal.aborted) {
      try {
        const res = await apiFetch("/api/attention/stream", {
          method: "GET",
          headers: { accept: "text/event-stream" },
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) {
          await sleep(reconnectDelay(attempt++), ctrl.signal);
          continue;
        }
        attempt = 0;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!ctrl.signal.aborted) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let boundary = buffer.indexOf("\n\n");
          while (boundary !== -1) {
            const raw = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            const parsed = parseSseEvent(raw);
            if (parsed?.event === "attention" && parsed.data) {
              try {
                broadcast(JSON.parse(parsed.data) as AttentionState);
              } catch {
                /* malformed payload */
              }
            }
            boundary = buffer.indexOf("\n\n");
          }
        }
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
      }
      if (!ctrl.signal.aborted) await sleep(reconnectDelay(attempt++), ctrl.signal);
    }
  };

  void run();
}

function stopStream(): void {
  controller?.abort();
  controller = null;
}

export function useAttentionEvents(options: UseAttentionEventsOptions = {}) {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<AttentionState | undefined>(latest);

  useEffect(() => {
    if (!enabled) {
      setData(undefined);
      return;
    }
    listeners.add(setData);
    setData(latest);
    refCount = refCount + 1;
    if (stopTimer !== null) {
      clearTimeout(stopTimer);
      stopTimer = null;
    }
    startStream();

    return () => {
      listeners.delete(setData);
      refCount = refCount - 1;
      if (refCount === 0) {
        // Deferred so a StrictMode unmount/remount doesn't tear down and reopen.
        stopTimer = setTimeout(() => {
          stopStream();
          broadcast(undefined);
          stopTimer = null;
        }, STOP_GRACE_MS);
      }
    };
  }, [enabled]);

  return { data };
}

function reconnectDelay(attempt: number): number {
  return RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)] ?? 15000;
}

async function sleep(ms: number, signal: AbortSignal): Promise<void> {
  await new Promise<void>((resolve) => {
    if (signal.aborted) return resolve();
    // The global timers, not window's: the stop timer can abort a stream after
    // a test environment has torn its window down, and the abort lands here.
    const t = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(t);
      resolve();
    });
  });
}

function parseSseEvent(raw: string): { event?: string; data?: string } | null {
  if (!raw.trim()) return null;
  const out: { event?: string; data?: string } = {};
  for (const line of raw.split("\n")) {
    if (line.startsWith("event:")) out.event = line.slice(6).trim();
    else if (line.startsWith("data:")) out.data = (out.data ?? "") + line.slice(5).trim();
  }
  return out;
}
